import { createStyles, Highlight, Paper, Stack } from '@mantine/core';
import React from 'react';
import { Link } from 'react-router-dom';

import { SettingPage } from './settings-page.types';

interface SettingCardProps {
  icon: SettingPage['icon'];
  title: SettingPage['title'];
  description: SettingPage['description'];
  path: SettingPage['path'];
  ribbon?: SettingPage['ribbon'];
  badge?: SettingPage['badge'];
  searchTerm: string;
  isHighlighted: boolean;
}

export function SettingCard({
  icon,
  title,
  description,
  path,
  ribbon,
  badge,
  isHighlighted,
  searchTerm,
}: SettingCardProps) {
  const { classes } = useStyles(isHighlighted);

  return (
    <Paper
      component={Link}
      to={path}
      withBorder
      radius="md"
      className={classes.container}
    >
      {badge && <div className={classes.badge}>{badge}</div>}

      <>{ribbon}</>

      <Stack spacing="xs" className={classes.content}>
        <div className={classes.iconContainer}>{icon}</div>

        <Highlight
          className={classes.highlight}
          highlight={searchTerm}
          size="lg"
          color="gray.9"
          weight={500}
          data-testid={`settings-card-title-${path}`}
        >
          {title}
        </Highlight>

        <Highlight
          color="gray.6"
          className={classes.highlight}
          highlight={searchTerm}
        >
          {description}
        </Highlight>
      </Stack>
    </Paper>
  );
}

const useStyles = createStyles((theme, isHighlighted: boolean) => ({
  container: {
    position: 'relative',
    paddingTop: 40,
    paddingInline: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    transition: 'all 150ms linear',
    opacity: isHighlighted ? 1 : 0.4,

    '&:hover': {
      boxShadow: theme.shadows.lg,
      textDecoration: 'none',
    },
  },
  badge: {
    position: 'absolute',
    top: theme.spacing.xl,
    left: theme.spacing.xl,

    color: theme.colors.blue[3],
  },
  content: {
    height: '100%',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconContainer: {
    color: theme.colors.blue[3],

    svg: {
      width: 32,
      height: 32,
    },
  },
  highlight: {
    mark: {
      padding: 0,
    },
  },
}));
